import React, { useEffect, useMemo, useRef, useState } from 'react';
import './andscene.css';
import AudioPlayer from '../AudioPlayer';
import andSceneTheme from '../../sound/andscene/theme.mp3';

export default function AndScenePlayer({ socket, sessionId, playerName, players, isMusicMuted = false }) {
  const [state, setState] = useState({
    phase: 'lobby',
    writingDeadline: null,
    writingPrompts: [],
    playerPromptOrders: {},
    locationAssignments: {},
    starterAssignments: {},
    playerWritingStatus: {},
    linePool: [],
    scenesPublic: [],
    currentMatch: null,
    completedMatches: [],
    scoreboard: [],
    roundSummary: null,
    votingLocked: false,
    voteReveal: null,
  });

  const [promptIdx, setPromptIdx] = useState(0);
  const [currentLine, setCurrentLine] = useState('');
  const [writingDone, setWritingDone] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const writingDoneEmitted = useRef(false);
  const submittedCountRef = useRef(0);

  const [sceneSlots, setSceneSlots] = useState(Array(5).fill(null));
  const [submittedScene, setSubmittedScene] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);

  const [myVote, setMyVote] = useState(null);

  const prevPhase = useRef(null);

  const me = useMemo(
    () => state.scoreboard.find((e) => e.name === playerName),
    [state.scoreboard, playerName],
  );
  const mySocketId = me?.id;

  const myLocation = mySocketId ? (state.locationAssignments?.[mySocketId] || null) : null;
  const myStarterLine = mySocketId ? (state.starterAssignments?.[mySocketId] || null) : null;
  const myPromptOrder = mySocketId ? (state.playerPromptOrders?.[mySocketId] || []) : [];
  const totalWritingPrompts = myPromptOrder.length || (state.writingPrompts || []).length;

  const currentPromptId = myPromptOrder[promptIdx] || null;
  const currentPrompt = useMemo(
    () => (state.writingPrompts || []).find((prompt) => prompt.id === currentPromptId) || null,
    [state.writingPrompts, currentPromptId],
  );

  const speakerOptions = useMemo(() => {
    const names = (players || []).map((p) => p.name);
    if (myStarterLine?.speakerName) names.push(myStarterLine.speakerName);
    return [...new Set(names.filter(Boolean))];
  }, [players, myStarterLine]);

  const maxSubmissions = useMemo(
    () => Math.min(
      totalWritingPrompts,
      2 * Math.max((state.scoreboard || []).length, 1),
    ),
    [totalWritingPrompts, state.scoreboard],
  );

  const writingPromptCap = useMemo(() => {
    if (maxSubmissions > 0) return maxSubmissions;
    return Math.max(totalWritingPrompts, 1);
  }, [totalWritingPrompts, maxSubmissions]);

  const slottedIds = useMemo(
    () => new Set(sceneSlots.filter(Boolean).map((slot) => slot.id)),
    [sceneSlots],
  );

  const availableLines = useMemo(
    () => (state.linePool || []).filter(
      (line) => line.authorId !== mySocketId && !slottedIds.has(line.id),
    ),
    [state.linePool, mySocketId, slottedIds],
  );

  const amInCurrentMatch = useMemo(() => {
    if (!mySocketId || !state.currentMatch) return false;
    return (
      state.currentMatch.a?.ownerId === mySocketId
      || state.currentMatch.b?.ownerId === mySocketId
    );
  }, [mySocketId, state.currentMatch]);

  const getLineText = (lineId) => (state.linePool || []).find((l) => l.id === lineId)?.text || '';
  const getSceneLineText = (line) => line?.text || getLineText(line?.id) || '';

  const updateSceneSlotSpeaker = (slotIdx, speakerName) => {
    setSceneSlots((prev) => prev.map((slot, idx) => (
      idx === slotIdx && slot
        ? { ...slot, speakerName }
        : slot
    )));
  };

  useEffect(() => {
    if (!socket) return undefined;
    const update = (incoming) => setState((prev) => ({ ...prev, ...incoming }));
    socket.on('andSceneState', update);
    socket.emit('andSceneSyncState', { sessionId });
    return () => socket.off('andSceneState', update);
  }, [socket, sessionId]);

  useEffect(() => {
    if (prevPhase.current === state.phase) return;
    prevPhase.current = state.phase;

    if (state.phase === 'writing') {
      setPromptIdx(0);
      setCurrentLine('');
      setWritingDone(false);
      setTimeLeft(60);
      writingDoneEmitted.current = false;
      submittedCountRef.current = 0;
    }

    if (state.phase === 'editing') {
      const starterLine = myStarterLine ? {
        ...myStarterLine,
        speakerName: myStarterLine.speakerName || myStarterLine.starterSpeaker || 'Narrator',
      } : null;
      setSceneSlots([starterLine, null, null, null, null]);
      setSubmittedScene(false);
      setSelectedLine(null);
    }

    if (state.phase === 'voting') {
      setMyVote(null);
    }
  }, [state.phase, myStarterLine]);

  useEffect(() => {
    if (state.phase !== 'voting') return;
    setMyVote(null);
  }, [state.currentMatch?.id, state.phase]);

  useEffect(() => {
    if (state.phase !== 'writing' || !state.writingDeadline || writingDone) return;

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((state.writingDeadline - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining === 0) markWritingDone();
    };

    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.writingDeadline, writingDone]);

  const markWritingDone = () => {
    if (writingDoneEmitted.current) return;
    writingDoneEmitted.current = true;
    setWritingDone(true);
    socket.emit('andSceneMarkWritingDone', { sessionId });
  };

  const handleSubmitPrompt = (forceSkip = false) => {
    if (!currentPrompt) return;

    const trimmedLine = currentLine.trim();
    const hasLine = trimmedLine.length > 0;

    socket.emit('andSceneSubmitLines', {
      sessionId,
      promptId: currentPrompt.id,
      line: trimmedLine,
      skip: forceSkip || !hasLine,
    });

    if (hasLine) submittedCountRef.current += 1;

    const nextIdx = promptIdx + 1;
    const done = submittedCountRef.current >= maxSubmissions || nextIdx >= totalWritingPrompts;

    if (done) {
      markWritingDone();
    } else {
      setPromptIdx(nextIdx);
      setCurrentLine('');
    }
  };

  const handleLineTap = (lineId) => {
    const line = availableLines.find((candidate) => candidate.id === lineId)
      || sceneSlots.find((candidate) => candidate?.id === lineId)
      || (myStarterLine?.id === lineId ? myStarterLine : null);

    if (!line) return;

    if (selectedLine?.id === lineId) {
      setSelectedLine(null);
      return;
    }

    setSceneSlots((prev) => {
      const next = [...prev];
      const existingSlot = next.findIndex((slot) => slot?.id === lineId);
      if (existingSlot !== -1) next[existingSlot] = null;
      return next;
    });

    setSelectedLine({ ...line });
  };

  const handleSlotTap = (slotIdx) => {
    if (selectedLine) {
      setSceneSlots((prev) => {
        const next = [...prev];
        const displaced = next[slotIdx];
        next[slotIdx] = {
          ...selectedLine,
          speakerName: selectedLine.speakerName || '',
        };
        setSelectedLine(displaced ? { ...displaced } : null);
        return next;
      });
    } else if (sceneSlots[slotIdx]) {
      setSelectedLine({ ...sceneSlots[slotIdx] });
      setSceneSlots((prev) => {
        const next = [...prev];
        next[slotIdx] = null;
        return next;
      });
    }
  };

  const handleRemoveFromSlot = (slotIdx, e) => {
    e.stopPropagation();
    setSceneSlots((prev) => {
      const next = [...prev];
      next[slotIdx] = null;
      return next;
    });
    setSelectedLine(null);
  };

  const handleSubmitScene = () => {
    if (submittedScene) return;
    const sceneLines = sceneSlots.filter(Boolean).map((line) => ({
      lineId: line.id,
      speakerName: line.speakerName || '',
    }));
    socket.emit('andSceneSubmitScene', { sessionId, sceneLines });
    setSubmittedScene(true);
  };

  const handleVote = (sceneId) => {
    if (myVote || amInCurrentMatch || state.votingLocked) return;
    socket.emit('andSceneVote', { sessionId, sceneId });
    setMyVote(sceneId);
  };

  const timerClass = timeLeft <= 10
    ? 'andscene-player-timer-danger'
    : timeLeft <= 20
      ? 'andscene-player-timer-warn'
      : 'andscene-player-timer-ok';
  const isThemePlaying = state.phase === 'lobby' || state.phase === 'writing';

  const SceneDisplay = ({ scene, showVoteButton }) => {
    const cardClass = [
      'andscene-player-vote-card',
      myVote === scene.id ? 'andscene-player-vote-card-selected' : '',
    ].filter(Boolean).join(' ');

    return (
      <div className={cardClass}>
        <p className="andscene-player-vote-location">📍 {scene.location}</p>
        {(scene.lines || []).length === 0 && (
          <p className="andscene-player-vote-empty">(empty scene)</p>
        )}
        {(scene.lines || []).map((line) => (
          <p key={line.id} className="andscene-player-vote-line">
            <strong>{line.speakerName || 'Unknown'}:</strong> {line.text}
          </p>
        ))}
        <p className="andscene-player-vote-end">🎬 AND SCENE</p>
        {showVoteButton && (
          <button
            onClick={() => handleVote(scene.id)}
            disabled={!!myVote}
            className="andscene-player-vote-btn"
          >
            {myVote === scene.id ? '✅ Voted' : 'Vote for this scene'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="andscene-player-root">
      <AudioPlayer audioSrc={andSceneTheme} isPlaying={isThemePlaying} isMuted={isMusicMuted} />
      <div className="App andscene-player-hero">
        <h2 className="andscene-player-hero-title">And Scene</h2>
        <span className="andscene-player-hero-score">{playerName} · {me?.points ?? 0} pts</span>
      </div>

      <div className="andscene-player-main">
        <div className="App andscene-player-panel">
          {state.phase === 'lobby' && (
            <div>
              <h3>Waiting for host to start…</h3>
              <p className="andscene-player-subtle">Get ready to write some scenes.</p>
            </div>
          )}

          {state.phase === 'writing' && (
            writingDone ? (
              <div className="andscene-player-center">
                <h2>Nice work!</h2>
                <p>Your lines are in the pool. Waiting for everyone to finish…</p>
              </div>
            ) : (
              <div>
                <div className="andscene-player-writing-top">
                  <span className="andscene-player-progress">
                    Responses {submittedCountRef.current} / {writingPromptCap}
                  </span>
                  <span className={`andscene-player-timer ${timerClass}`}>{timeLeft}s</span>
                </div>

                {currentPrompt && (
                  <div className="andscene-player-prompt-card">
                    <p className="andscene-player-prompt-setup">{currentPrompt.setup}</p>
                    <p className="andscene-player-prompt-line">
                      <strong>{currentPrompt.starter?.speaker || 'Narrator'}:</strong> {currentPrompt.starter?.text}
                    </p>
                  </div>
                )}

                <p className="andscene-player-writing-help">Write one follow-up line for this prompt:</p>
                <input
                  type="text"
                  value={currentLine}
                  placeholder="Your follow-up line"
                  onChange={(e) => setCurrentLine(e.target.value)}
                  className="andscene-player-input"
                />

                <div className="andscene-player-writing-actions">
                  <button
                    onClick={() => handleSubmitPrompt(false)}
                    className="andscene-player-submit"
                    disabled={currentLine.trim().length === 0}
                  >
                    {submittedCountRef.current >= Math.max(writingPromptCap - 1, 0)
                      ? 'Submit & Finish 🎉'
                      : 'Submit'}
                  </button>
                  <button
                    onClick={() => handleSubmitPrompt(true)}
                    className="andscene-player-skip"
                  >
                    Skip
                  </button>
                </div>
              </div>
            )
          )}

          {state.phase === 'editing' && (
            submittedScene ? (
              <div className="andscene-player-center">
                <h2>✅ Scene locked in!</h2>
                <p>Waiting for everyone to finish building…</p>
              </div>
            ) : (
              <div>
                <h3 className="andscene-player-heading">Build Your Scene</h3>
                <p className="andscene-player-editing-help">
                  Tap a line below to select it, then tap a slot to place it. You can't use your own lines.
                  {selectedLine && ' 👇 Now tap a slot to place the selected line.'}
                </p>

                <div className="andscene-player-scene-card">
                  <div className="andscene-player-scene-header">📍 {myLocation || '…'}</div>

                  {Array.from({ length: 5 }, (_, idx) => {
                    const line = sceneSlots[idx];
                    return (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={idx}
                        onClick={() => handleSlotTap(idx)}
                        className="andscene-player-scene-slot"
                      >
                        {line ? (
                          <div className="andscene-player-scene-slot-line">
                            <select
                              value={line.speakerName || ''}
                              onChange={(e) => updateSceneSlotSpeaker(idx, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="andscene-player-speaker-select"
                            >
                              <option value="">Who says this?</option>
                              {speakerOptions.map((name) => (
                                <option key={name} value={name}>{name}</option>
                              ))}
                            </select>

                            <span className="andscene-player-scene-line">
                              <strong>{line.speakerName || 'Speaker'}:</strong> {getSceneLineText(line)}
                              {line.isStarter && <span className="andscene-player-opening-tag"> [opening]</span>}
                            </span>

                            <button
                              onClick={(e) => handleRemoveFromSlot(idx, e)}
                              className="andscene-player-remove"
                              aria-label="Remove line"
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <span className="andscene-player-scene-slot-empty">
                            {selectedLine ? '← tap to place here' : `Slot ${idx + 1}`}
                          </span>
                        )}
                      </div>
                    );
                  })}

                  <div className="andscene-player-scene-footer">🎬 AND SCENE</div>
                </div>

                <h4 className="andscene-player-available-title">Available Lines</h4>
                {availableLines.length === 0 && (
                  <p className="andscene-player-available-empty">No lines to pick from yet.</p>
                )}

                <div className="andscene-player-available-list">
                  {availableLines.map((line) => {
                    const selected = selectedLine?.id === line.id;
                    return (
                      <div
                        key={line.id}
                        onClick={() => handleLineTap(line.id)}
                        className={[
                          'andscene-player-available-line',
                          selected ? 'andscene-player-available-line-selected' : '',
                        ].filter(Boolean).join(' ')}
                      >
                        {selected && (
                          <span className="andscene-player-available-check">✓</span>
                        )}
                        <span>{line.text}</span>
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={handleSubmitScene}
                  className="andscene-player-lock-btn"
                  disabled={sceneSlots.every((s) => !s)}
                >
                  Lock In Scene
                </button>
              </div>
            )
          )}

          {state.phase === 'voting' && (
            <div>
              {state.votingLocked ? (
                <div className="andscene-player-voting-wait">
                  <h3>🎬 Vote closed</h3>
                  <p className="andscene-player-voted-wrap">Showing result and point awards...</p>
                </div>
              ) : amInCurrentMatch ? (
                <div className="andscene-player-voting-wait">
                  <h3>Your scene is on stage! 🎭</h3>
                  <p className="andscene-player-voted-wrap">You can't vote while your scene is competing. Waiting for the results…</p>
                </div>
              ) : myVote ? (
                <div>
                  <h3>✅ Vote cast! Waiting for others…</h3>
                  {state.currentMatch && [state.currentMatch.a, state.currentMatch.b]
                    .filter((s) => s && s.id === myVote)
                    .map((scene) => (
                      <div key={scene.id} className="andscene-player-voted-wrap">
                        <p className="andscene-player-voted-label">You voted for:</p>
                        <SceneDisplay scene={scene} showVoteButton={false} />
                      </div>
                    ))}
                </div>
              ) : state.currentMatch ? (
                <div>
                  <h3>Vote for the best scene</h3>
                  {[state.currentMatch.a, state.currentMatch.b].filter(Boolean).map((scene) => (
                    <SceneDisplay key={scene.id} scene={scene} showVoteButton />
                  ))}
                </div>
              ) : (
                <p>Waiting for next match…</p>
              )}
            </div>
          )}

          {state.phase === 'round-end' && (
            <div>
              <h2>🏆 Round Complete!</h2>
              {state.roundSummary?.winnerScene && (
                <>
                  <h3>Winning Scene</h3>
                  <SceneDisplay scene={state.roundSummary.winnerScene} showVoteButton={false} />
                </>
              )}

              <h3>Scores</h3>
              {[...(state.scoreboard || [])].sort((a, b) => b.points - a.points).map((entry) => (
                <div key={entry.id} className="andscene-player-score-row">
                  <span>{entry.name}</span>
                  <strong>{entry.points} pts</strong>
                </div>
              ))}
              <p className="andscene-player-round-note">{state.roundSummary?.pointsRule}</p>
              <p className="andscene-player-host-wait">Waiting for host to start the next round…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
